import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { SagView } from '../../../cases/models';
import { TimeBudget } from '../../../../../timetrack/store/models';
import { IndexCard } from '../../../../../auth/store/models';
import { TimesatsNavne } from '@store/models';
import { first, tap } from 'rxjs/operators';
import { CasesDispatchers, CasesService } from '../../../cases/store/services';
import { ClientService } from '../../../store/services';
import { AuthSelectors, SessionSelectors, TimesatsNavneService } from '@store';
import { AlertService } from '../../../../../core/alerts/alert.service';

export interface ResourceplanState {
    la: number;
    ka: number;
    sag: SagView;
    timeBudgets: TimeBudget[];
    caseUserIds: number[];
    timesatsNavne: TimesatsNavne[];
    sagsansvarlige: IndexCard[];
    user: IndexCard;
}

const initialState: ResourceplanState = {
    la: null,
    ka: null,
    sag: null,
    timeBudgets: [],
    caseUserIds: [],
    timesatsNavne: [],
    sagsansvarlige: [],
    user: null,
};

@Injectable()
export class ResourceplanStore extends ComponentStore<ResourceplanState> {
    constructor(
        private casesService: CasesService,
        private clientService: ClientService,
        private sessionSelector: SessionSelectors,
        private timesatsNavneService: TimesatsNavneService,
        private authSelector: AuthSelectors,
        private alert: AlertService,
        private casesDispatch: CasesDispatchers,
    ) {
        super(initialState);
    }

    readonly vm$ = this.select(state => state);

    readonly users$ = this.select(this.state$, this.sessionSelector.users$(new Date()), (state, users) => {
        const nonMandatoryUsers = users.filter(
            ic => ic.cardId !== state.la && ic.cardId !== state.ka && ic.cardId !== state.sag?.sa
        );
        return nonMandatoryUsers;
    });

    readonly caseUsers$ = this.select(this.state$, this.sessionSelector.users$(new Date()), (state, users) => {
        const caseUserIdsToInclude = [...state.caseUserIds, state.la, state.ka, state.sag?.sa];
        // const filterdUsers = users.filter(user => caseUserIdsToInclude.includes(user.cardId));
        const filterdUsers = caseUserIdsToInclude
            .filter((v, i, a) => a.indexOf(v) === i)
            .map(id => users.find(user => user.cardId === id) ?? {cardId: id, navn: 'Ukendt'});
        const sortIndex = [state.sag?.sa, state.ka, state.la];
        const sortedCaseUsers = filterdUsers.sort((a, b) => sortIndex.indexOf(b.cardId) - sortIndex.indexOf(a.cardId));
        return sortedCaseUsers;
    });

    readonly createTimeBudgetState = this.updater((state, timeBudget: TimeBudget) => ({
        ...state,
        timeBudgets: [...state.timeBudgets, timeBudget]
    }));

    readonly updateTimeBudgetState = this.updater((state, updatedTimeBudget: TimeBudget) => ({
        ...state,
        timeBudgets: [
            ...state.timeBudgets.filter(tb => tb.oid !== updatedTimeBudget.oid),
            updatedTimeBudget
        ]
    }));

    readonly deleteTimeBudgetState = this.updater((state, id: number) => ({
        ...state,
        timeBudgets: state.timeBudgets.filter(tb => tb.oid !== id)
    }));

    readonly addCaseUser = this.updater((state, userToAdd: number) => ({
        ...state,
        caseUserIds: [...state.caseUserIds, userToAdd]
    }));

    readonly removeCaseUser = this.updater((state, userToRemove: number) => ({
        ...state,
        caseUserIds: state.caseUserIds.filter(id => id !== userToRemove)
    }));

    init(sagId: number) {
        this.loadSag(sagId);
        this.loadTimesatsNavne();
        this.loadUser();
    }

    private loadTimesatsNavne() {
        this.timesatsNavneService.getTimesatsNavne().subscribe(timesatsNavne => {
            this.patchState({ timesatsNavne });
        });
    }

    private loadSag(sagId: number) {
        this.casesService.getCase(sagId).subscribe(sag => {
            this.patchState({ sag });
            this.loadLaKa(sag);
            this.loadSagsansvarlige(sag);
        });
    }

    private loadLaKa(sag: SagView) {
        this.clientService.getKunde(sag.kid).subscribe(kunde => {
            this.patchState( { la: kunde.ledelsesansvarlig, ka: kunde.kundeansvarlig });
            this.loadTimeBudgets(sag, kunde.ledelsesansvarlig, kunde.kundeansvarlig);
        });
    }

    private loadTimeBudgets(sag: SagView, la: number, ka: number) {
        this.casesService.loadTimeBudgets(sag.pid).subscribe(timeBudgets => {
            this.patchState({ timeBudgets });
            const userIds = timeBudgets.map(timeBudget => timeBudget.medID);
            const caseUserIds = userIds.filter((v, i, a) => a.indexOf(v) === i);
           this.loadNonMandatoryCaseUsers(caseUserIds, sag, la, ka);
        });
    }

    private loadSagsansvarlige(sag: SagView) {
        this.sessionSelector.usersSA$(new Date()).pipe(first()).subscribe(sagsansvarlige => {
            const currentSa = sagsansvarlige.find(s => s.cardId === sag.sa);
            if (!currentSa) {
                this.patchState({ sagsansvarlige: [
                    {cardId: sag.sa, navn: 'Ukendt / fratrådt'} as IndexCard,
                    ...sagsansvarlige
                ]});
            } else {
                this.patchState({ sagsansvarlige });
            }
        });
    }

    private loadNonMandatoryCaseUsers(caseUserIds: number[], sag: SagView, la: number, ka: number) {
        const result = caseUserIds.filter(
            id => id !== la && id !== ka && id !== sag?.sa
        );
        this.patchState({ caseUserIds: result });
    }

    private loadUser() {
        this.authSelector.userCard$.pipe(first()).subscribe(user => {
            this.patchState({ user });
        });
    }

    createTimeBudget(timeBudget: TimeBudget) {
        return this.casesService.createTimeBudget(timeBudget).pipe(
            first(),
            tap(newTimebudget => this.createTimeBudgetState(newTimebudget)),
        );
    }

    updateTimeBudget(timeBudget: TimeBudget) {
        return this.casesService.updateTimeBudget(timeBudget).pipe(
            first(),
            tap(updatedTimeBudget => this.updateTimeBudgetState(updatedTimeBudget))
        );
    }

    deleteTimeBudget(id: number) {
        return this.casesService.deleteTimeBudget(id).pipe(
            first(),
            tap(() => this.deleteTimeBudgetState(id)),
        );
    }

    updateClientCaseV2(sag: SagView, partialSag: Partial<SagView>) {
        return this.casesService.updateClientCaseV2(sag.v2RegnskabsaarKundeId, sag.pid, partialSag).pipe(
            first(),
            tap(updatedSag => {
                if (updatedSag.alert) {
                    const alertLines = sag.alert.split('\n');

                    alertLines.forEach((line) => {
                        this.alert.createAlert(line, 'warning');
                    });
                }
                this.patchState({ sag: updatedSag });
                this.casesDispatch.loadCases();
            })
        );
    }
}
